import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, CardAction } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose, DialogTrigger } from '@/components/ui/dialog';
import type { SessionTemplate } from '@arrowise/shared';

export function SessionTemplateItem({ template }: { template: SessionTemplate }) {
    const [open, setOpen] = useState(false);
    return (
        <Card className="hover:shadow-lg transition-shadow duration-200">
            <CardHeader>
                <CardTitle>{template.name}</CardTitle>
                <CardAction>
                    {template.isPublic ? (
                        <span className="px-2 py-1 rounded bg-accent text-accent-foreground text-xs font-semibold">Public</span>
                    ) : (
                        <span className="px-2 py-1 rounded bg-secondary text-secondary-foreground text-xs font-semibold">Team</span>
                    )}
                </CardAction>
            </CardHeader>
            <CardContent>
                <CardDescription className="relative group">
                    {template.description || 'No description provided.'}
                </CardDescription>
                <div className="mt-4 space-y-2 text-sm">
                    <div><span className="font-medium">Language:</span> {template.language}</div>
                    <div><span className="font-medium">Difficulty:</span> <span className={`inline-block px-2 py-0.5 rounded ${template.difficulty === 'junior' ? 'bg-green-100 text-green-800' : template.difficulty === 'mid' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>{template.difficulty}</span></div>
                    <div><span className="font-medium">Duration:</span> {template.estimatedDuration} min</div>
                    {template.tags && template.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {template.tags.map(tag => (
                                <span key={tag} className="bg-muted text-muted-foreground px-2 py-0.5 rounded text-xs">{tag}</span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="mt-4">
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button variant="outline" size="sm">View Steps</Button>
                        </DialogTrigger>
                        <DialogContent showCloseButton>
                            <DialogHeader>
                                <DialogTitle>Session Steps</DialogTitle>
                                <DialogDescription>
                                    Steps for <span className="font-semibold">{template.name}</span>
                                </DialogDescription>
                            </DialogHeader>
                            {template.steps && template.steps.length > 0 ? (
                                <ol className="list-decimal pl-4 space-y-4">
                                    {template.steps.map((step) => (
                                        <li key={step.id} className="mb-2">
                                            <div className="font-medium">{step.title}</div>
                                            {step.description && (
                                                <div className="text-muted-foreground text-xs mt-1 whitespace-pre-line">{step.description}</div>
                                            )}
                                        </li>
                                    ))}
                                </ol>
                            ) : (
                                <div className="text-muted-foreground">No steps found for this template.</div>
                            )}
                            <DialogClose asChild>
                                <Button variant="secondary" className="mt-4">Close</Button>
                            </DialogClose>
                        </DialogContent>
                    </Dialog>
                </div>
            </CardContent>
            <CardFooter>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Created {new Date(template.createdAt).toLocaleDateString()}</span>
                    {template.team && (
                        <span className="ml-2">Team: <span className="font-medium">{template.team.name}</span></span>
                    )}
                </div>
            </CardFooter>
        </Card>
    );
}
